// ABOUTME: Glossary page explaining hypothesis analysis terminology and concepts
// ABOUTME: Provides definitions for key terms used in the research framework

export const GlossaryPage = () => {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Research Glossary</h1>
        <p className="text-gray-600">
          Key terms and concepts used in our hypothesis analysis framework for the FIG Learning-Design CoE interview study
        </p>
      </div>

      {/* Core Concepts */}
      <GlossarySection title="Core Concepts">
        <GlossaryTerm
          term="Hypothesis"
          definition="A testable statement or assumption about learning design, pedagogy, curriculum quality or AI across FIG that we validate or refute using evidence from staff interviews."
        />
        <GlossaryTerm
          term="Research Area"
          definition="The themed study (FIG Learning-Design CoE) that groups related hypotheses together."
        />
        <GlossaryTerm
          term="Evidence"
          definition="Specific quotes, observations, or data points from FIG staff interviews that either support or refute a hypothesis. Each piece of evidence includes the original quote, analytical reasoning, and metadata."
        />
        <GlossaryTerm
          term="Group"
          definition="The functional team a participant belongs to (e.g., Learning Designers, QA, Faculty/SMEs, Senior Leaders). Known intake metadata, captured at interview time."
        />
      </GlossarySection>

      {/* Evidence Classification */}
      <GlossarySection title="Evidence Classification">
        <GlossaryTerm
          term="Support"
          definition="Evidence that validates or confirms the hypothesis statement. Shown with a green badge and border."
          badge="support"
        />
        <GlossaryTerm
          term="Refute"
          definition="Evidence that contradicts or disproves the hypothesis statement. Shown with a red badge and border."
          badge="refute"
        />
        <GlossaryTerm
          term="Mixed"
          definition="Evidence that contains both supporting and contradicting elements, or suggests conditional validity. Shown with a yellow badge and border."
          badge="mixed"
        />
        <GlossaryTerm
          term="Neutral"
          definition="Evidence that is relevant to the hypothesis but neither clearly supports nor refutes it. Shown with a gray badge and border."
          badge="neutral"
        />
      </GlossarySection>

      {/* Evidence Strength */}
      <GlossarySection title="Evidence Strength">
        <GlossaryTerm
          term="Strong Evidence"
          definition="Clear, specific, and directly relevant statements that provide compelling support or refutation. Multiple specific examples or explicit causal relationships."
        />
        <GlossaryTerm
          term="Moderate Evidence"
          definition="Relevant but less explicit evidence that suggests patterns or trends. May require some interpretation or appear indirectly in the data."
        />
        <GlossaryTerm
          term="Weak Evidence"
          definition="Tangentially related or ambiguous evidence that provides minimal insight. May be vague, secondhand, or require significant interpretation."
        />
      </GlossarySection>

      {/* Analysis Framework */}
      <GlossarySection title="Analysis Framework">
        <GlossaryTerm
          term="Verdict"
          definition="The overall assessment of a hypothesis based on evidence patterns. Can be: Strongly Validated, Conditionally Validated, Mixed Evidence, Largely Refuted, or Strongly Refuted."
        />
        <GlossaryTerm
          term="Confidence Level"
          definition="How certain we are in the verdict, based on evidence quality, quantity, and consistency. Rated as High, Medium, or Low."
        />
        <GlossaryTerm
          term="Signal Analysis"
          definition="Identification of patterns in the evidence that indicate truth or falsehood. Includes expected positive/negative signals, unexpected discoveries, and notable absences."
        />
        <GlossaryTerm
          term="Controversial Score"
          definition="A metric (0-1) indicating how divided the evidence is. 0 means unanimous support/refutation, 1 means perfectly split evidence. Helps identify nuanced or conditional hypotheses."
        />
        <GlossaryTerm
          term="Critical Evidence"
          definition="The most revealing quotes that exemplify broader patterns in the data. These are representative of trends, not cherry-picked outliers."
        />
      </GlossarySection>

      {/* Signal Types */}
      <GlossarySection title="Signal Types">
        <GlossaryTerm
          term="Expected Positive Signals"
          definition="Specific phrases, behaviors, or patterns we anticipate seeing if the hypothesis is true. Defined before analysis begins."
        />
        <GlossaryTerm
          term="Expected Negative Signals"
          definition="Specific phrases, behaviors, or patterns we anticipate seeing if the hypothesis is false. Defined before analysis begins."
        />
        <GlossaryTerm
          term="Matched Signals"
          definition="Template phrases that indicate evidence aligns with expected positive or negative signals. These are NOT validated as direct quotes but as pattern indicators."
        />
        <GlossaryTerm
          term="Unexpected Discoveries"
          definition="Patterns or insights that emerged from the data but were not anticipated in the original hypothesis framework."
        />
        <GlossaryTerm
          term="Notable Absences"
          definition="Expected signals that did not appear in the evidence. What's NOT mentioned can be as revealing as what IS said."
        />
      </GlossarySection>

      {/* Evidence Validation */}
      <GlossarySection title="Evidence Validation">
        <GlossaryTerm
          term="Valid Evidence"
          definition="Evidence that has been matched to a real interview source and can be traced back to the original transcript. Shown with a green 'Valid' badge."
          badge="valid"
        />
        <GlossaryTerm
          term="Unmatched Evidence"
          definition="AI-generated summary evidence that could not be matched to a specific interview source. Shown with an orange 'Unmatched' badge and is not clickable."
          badge="unmatched"
        />
        <GlossaryTerm
          term="Key Quote"
          definition="The exact words spoken by an interviewee that form the basis of an evidence item. Must be accurate and properly sanitized (no PII)."
        />
        <GlossaryTerm
          term="Answer Text"
          definition="The full response from the interviewee, providing context for the key quote. Used for validation and evidence tracing."
        />
      </GlossarySection>

      {/* Pattern Analysis */}
      <GlossarySection title="Pattern Analysis">
        <GlossaryTerm
          term="Convergent Patterns"
          definition="Evidence pointing in the same direction across different personas, contexts, or time periods. Indicates strong, consistent findings."
        />
        <GlossaryTerm
          term="Divergent Patterns"
          definition="Conflicting evidence that reveals nuance. Analysis focuses on WHY evidence conflicts rather than dismissing outliers."
        />
        <GlossaryTerm
          term="Contextual Factors"
          definition="External circumstances that influence evidence interpretation: geographic variations, economic conditions, cultural factors, or temporal patterns."
        />
        <GlossaryTerm
          term="Persona-Specific Insights"
          definition="Unique patterns or experiences that are specific to a particular learner archetype. Different personas may have systematically different experiences."
        />
      </GlossarySection>

      {/* Business Outputs */}
      <GlossarySection title="Business Outputs">
        <GlossaryTerm
          term="Executive Summary"
          definition="A 2-3 sentence synthesis that captures the core finding with nuance, suitable for leadership review."
        />
        <GlossaryTerm
          term="Business Recommendations"
          definition="Actionable insights divided into Immediate Actions (quick wins), Strategic Considerations (long-term implications), Risk Factors (critical issues), and Success Amplification (patterns to scale)."
        />
        <GlossaryTerm
          term="Informing Questions"
          definition="The specific interview questions that generated the evidence for a hypothesis. Helps understand the context and framing of responses."
        />
      </GlossarySection>

      {/* Quality & Methodology */}
      <GlossarySection title="Quality & Methodology">
        <GlossaryTerm
          term="Evidence Quality"
          definition="Assessment of the overall strength, relevance, and reliability of the evidence base supporting a hypothesis."
        />
        <GlossaryTerm
          term="Coverage Gaps"
          definition="Personas, contexts, or perspectives that are underrepresented in the data, limiting the generalizability of findings."
        />
        <GlossaryTerm
          term="Confidence Limiters"
          definition="Factors that reduce certainty in conclusions, such as small sample sizes, conflicting evidence, or methodological constraints."
        />
        <GlossaryTerm
          term="Quality Controller"
          definition="Automated validation system that flags issues with evidence accuracy, particularly ensuring key_quotes and answer_text are properly validated."
        />
      </GlossarySection>
    </div>
  );
};

interface GlossarySectionProps {
  title: string;
  children: React.ReactNode;
}

const GlossarySection = ({ title, children }: GlossarySectionProps) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

interface GlossaryTermProps {
  term: string;
  definition: string;
  badge?: 'support' | 'refute' | 'mixed' | 'neutral' | 'valid' | 'unmatched';
}

const GlossaryTerm = ({ term, definition, badge }: GlossaryTermProps) => {
  const getBadgeStyles = () => {
    switch (badge) {
      case 'support':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'refute':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'mixed':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'neutral':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'valid':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'unmatched':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return '';
    }
  };

  return (
    <div className="border-l-4 border-purple-300 pl-4">
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-lg font-semibold text-gray-900">{term}</h3>
        {badge && (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold border ${getBadgeStyles()}`}
          >
            {badge.toUpperCase()}
          </span>
        )}
      </div>
      <p className="text-gray-700 leading-relaxed">{definition}</p>
    </div>
  );
};
